import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { SCHEDULE_E_CATEGORIES, useAccounting } from "@/hooks/use-accounting";
import { fmtMoney } from "@/components/accounting/accounting-format";
import { cn } from "@/lib/utils";

interface Props {
  year: number;
}

const INCOME_CATS = SCHEDULE_E_CATEGORIES.filter((c) => c.type === "income");
const EXPENSE_CATS = SCHEDULE_E_CATEGORIES.filter((c) => c.type === "expense");

interface LineRowProps {
  line: number | string;
  label: string;
  source?: string;
  amount: number;
  tone: "income" | "expense";
  variant?: "default" | "total" | "net";
}

function LineRow({ line, label, source, amount, tone, variant = "default" }: LineRowProps) {
  const zero = amount === 0 && variant === "default";
  const amountClass = zero
    ? "text-muted-foreground font-medium"
    : tone === "income"
      ? "text-success"
      : "text-destructive";
  return (
    <div
      className={cn(
        "grid grid-cols-[50px_1fr_160px] items-center gap-4 py-2.5",
        variant === "default" && "border-b border-dashed border-border last:border-b-0",
        variant === "total" && "mt-1 border-t border-input pt-3 font-bold",
        variant === "net" && "mt-2 border-t border-input pt-3.5 text-base font-extrabold",
      )}
    >
      <div className="rounded-md border border-border bg-muted/50 py-1 text-center text-[11px] font-bold text-muted-foreground">
        {line}
      </div>
      <div>
        <p className="text-[13px] text-foreground">{label}</p>
        {source && <p className="mt-0.5 text-[11.5px] text-muted-foreground">{source}</p>}
      </div>
      <div className={cn("text-right text-sm font-bold tabular-nums", amountClass)}>
        {zero ? "—" : fmtMoney(amount)}
      </div>
    </div>
  );
}

export function ScheduleETab({ year }: Props) {
  const { allTransactions, properties } = useAccounting(year);
  const [scope, setScope] = useState("all");

  const scopeTxns = useMemo(
    () => (scope === "all" ? allTransactions : allTransactions.filter((t) => t.pipeline_property_id === scope)),
    [allTransactions, scope],
  );

  const byKey = useMemo(() => {
    const totals: Record<string, number> = {};
    SCHEDULE_E_CATEGORIES.forEach((c) => (totals[c.key] = 0));
    scopeTxns.forEach((t) => {
      totals[t.category] = (totals[t.category] || 0) + t.amount;
    });
    return totals;
  }, [scopeTxns]);

  const totalIncome = INCOME_CATS.reduce((s, c) => s + (byKey[c.key] || 0), 0);
  const totalExpense = EXPENSE_CATS.reduce((s, c) => s + (byKey[c.key] || 0), 0);
  const net = totalIncome - totalExpense;

  const scopeLabel =
    scope === "all"
      ? `All ${properties.length} ${properties.length === 1 ? "property" : "properties"}`
      : properties.find((p) => p.id === scope)?.label ?? "Property";

  return (
    <Card className="space-y-5 p-5">
      <div>
        <h3 className="text-sm font-bold text-foreground">Schedule E · Supplemental Income and Loss</h3>
        <p className="text-xs text-muted-foreground">
          Part I — Income or Loss From Rental Real Estate. Pick a property tab to see its individual return.
        </p>
      </div>

      {/* Property tabs */}
      <div className="flex flex-wrap gap-1.5">
        {[{ id: "all", label: "All properties" }, ...properties].map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setScope(p.id)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
              scope === p.id
                ? "border-primary/30 bg-primary/10 text-primary"
                : "border-border bg-muted/50 text-muted-foreground hover:text-foreground",
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Income */}
      <div className="rounded-xl border border-border p-4">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Income · Part I</p>
          <p className="text-[11.5px] text-muted-foreground">{scopeLabel} · {year}</p>
        </div>
        {INCOME_CATS.map((c) => (
          <LineRow key={c.key} line={c.line} label={c.label} source={c.source} amount={byKey[c.key] || 0} tone="income" />
        ))}
      </div>

      {/* Expenses */}
      <div className="rounded-xl border border-border p-4">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Expenses · Lines 5-19</p>
        {EXPENSE_CATS.map((c) => (
          <LineRow key={c.key} line={c.line} label={c.label} source={c.source} amount={byKey[c.key] || 0} tone="expense" />
        ))}
        <LineRow line={20} label="Total expenses" source="Sum of lines 5 through 19" amount={totalExpense} tone="expense" variant="total" />
      </div>

      {/* Net */}
      <div className="rounded-xl border border-border p-4">
        <LineRow
          line={21}
          label="Income or (loss)"
          source="Rents received minus total expenses"
          amount={net}
          tone={net >= 0 ? "income" : "expense"}
          variant="net"
        />
      </div>
    </Card>
  );
}
